import React from 'react';
import { GanttConfig, TimeScale } from '../types/gantt';
import { GANTT_THEMES } from '../services/themes';
import { Palette, Calendar, CalendarDays, Sliders, Type } from 'lucide-react';

interface ThemeSelectorProps {
  config: GanttConfig;
  onConfigChange: (config: GanttConfig) => void;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  config,
  onConfigChange,
}) => {
  const handleFieldChange = <K extends keyof GanttConfig>(
    key: K,
    value: GanttConfig[K]
  ) => {
    onConfigChange({
      ...config,
      [key]: value,
    });
  };

  return (
    <div className="theme-selector-container">
      {/* Section 1: Timeline & Calendar */}
      <div className="settings-section">
        <div className="settings-section-title">
          <Calendar size={14} />
          <span>Timeline & Calendar</span>
        </div>

        <div className="settings-row-2col">
          {/* Title Input */}
          <div className="option-group">
            <label className="option-label">
              <Type size={13} />
              <span>Chart Title (Optional)</span>
            </label>
            <input
              type="text"
              className="input-text"
              placeholder="e.g. Project Roadmap"
              value={config.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
            />
          </div>

          {/* Time Scale */}
          <div className="option-group">
            <label className="option-label">
              <Calendar size={13} />
              <span>Time Scale</span>
            </label>
            <select
              className="input-select"
              value={config.timeScale}
              onChange={(e) =>
                handleFieldChange('timeScale', e.target.value as TimeScale)
              }
            >
              <option value="auto">Auto (Smart Detection)</option>
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
              <option value="quarters">Quarters</option>
            </select>
          </div>

          {/* Week Start Day Segmented Control */}
          <div className="option-group" style={{ gridColumn: 'span 2' }}>
            <label className="option-label">
              <CalendarDays size={13} />
              <span>Week Starts On</span>
            </label>
            <div className="segmented-control">
              <button
                type="button"
                className={`segmented-btn ${(config.weekStartDay || 'monday') === 'monday' ? 'active' : ''}`}
                onClick={() => handleFieldChange('weekStartDay', 'monday')}
              >
                <span>Monday (ISO)</span>
                <span className="segmented-btn-sub">Format: 2026-09-02</span>
              </button>
              <button
                type="button"
                className={`segmented-btn ${config.weekStartDay === 'sunday' ? 'active' : ''}`}
                onClick={() => handleFieldChange('weekStartDay', 'sunday')}
              >
                <span>Sunday (US)</span>
                <span className="segmented-btn-sub">Format: Sep 2</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Color Theme */}
      <div className="settings-section">
        <div className="settings-section-title">
          <Palette size={14} />
          <span>Color Theme</span>
        </div>
        <div className="theme-chips">
          {Object.values(GANTT_THEMES).map((theme) => {
            const isSelected = config.themeId === theme.id;
            return (
              <button
                key={theme.id}
                type="button"
                className={`theme-chip ${isSelected ? 'active' : ''}`}
                onClick={() => handleFieldChange('themeId', theme.id)}
                title={theme.name}
              >
                <span
                  className="theme-color-preview"
                  style={{ backgroundColor: theme.primary }}
                />
                <span>{theme.name}</span>
              </button>
            );
          })}

          {/* Custom Hex Color Box */}
          <div
            className={`custom-color-box ${config.themeId === 'custom' ? 'active' : ''}`}
            title="Click to select or edit custom color"
            onClick={() => {
              if (config.themeId !== 'custom') {
                onConfigChange({
                  ...config,
                  themeId: 'custom',
                  customColor: config.customColor || '#FDE047',
                });
              }
            }}
          >
            <input
              type="color"
              className="custom-color-picker"
              value={
                config.customColor && /^#[0-9A-Fa-f]{6}$/.test(config.customColor)
                  ? config.customColor
                  : '#FDE047'
              }
              onInput={(e) => {
                onConfigChange({
                  ...config,
                  themeId: 'custom',
                  customColor: (e.target as HTMLInputElement).value.toUpperCase(),
                });
              }}
              onChange={(e) => {
                onConfigChange({
                  ...config,
                  themeId: 'custom',
                  customColor: e.target.value.toUpperCase(),
                });
              }}
              title="Pick a custom color"
            />
            <input
              type="text"
              className="custom-color-hex"
              placeholder="#HEX"
              maxLength={7}
              value={config.customColor || ''}
              onChange={(e) => {
                let val = e.target.value.trim();
                if (val && !val.startsWith('#')) {
                  val = '#' + val;
                }
                onConfigChange({
                  ...config,
                  themeId: 'custom',
                  customColor: val.toUpperCase(),
                });
              }}
              onFocus={() => {
                if (config.themeId !== 'custom') {
                  onConfigChange({
                    ...config,
                    themeId: 'custom',
                    customColor: config.customColor || '#FDE047',
                  });
                }
              }}
              title="Enter custom hex code (e.g. #FDE047)"
            />
          </div>
        </div>
      </div>

      {/* Section 3: Display Options */}
      <div className="settings-section">
        <div className="settings-section-title">
          <Sliders size={14} />
          <span>Display Options</span>
        </div>
        <div className="display-toggles-grid">
          <label className="toggle-card">
            <input
              type="checkbox"
              checked={config.showBandedRows !== false}
              onChange={(e) =>
                handleFieldChange('showBandedRows', e.target.checked)
              }
            />
            <span className="toggle-card-label">Banded Rows</span>
          </label>

          <label className="toggle-card">
            <input
              type="checkbox"
              checked={config.showProgress !== false}
              onChange={(e) =>
                handleFieldChange('showProgress', e.target.checked)
              }
            />
            <span className="toggle-card-label">Show Progress</span>
          </label>

          <label className="toggle-card">
            <input
              type="checkbox"
              checked={config.showWeekNumbers !== false}
              onChange={(e) =>
                handleFieldChange('showWeekNumbers', e.target.checked)
              }
            />
            <span className="toggle-card-label">Week Numbers (W1...)</span>
          </label>

          <label className="toggle-card">
            <input
              type="checkbox"
              checked={config.showToday}
              onChange={(e) => handleFieldChange('showToday', e.target.checked)}
            />
            <span className="toggle-card-label">Today Marker</span>
          </label>

          <label className="toggle-card">
            <input
              type="checkbox"
              checked={config.showGridlines}
              onChange={(e) =>
                handleFieldChange('showGridlines', e.target.checked)
              }
            />
            <span className="toggle-card-label">Gridlines</span>
          </label>
        </div>
      </div>
    </div>
  );
};
